import { useCallback, useEffect, useMemo, useState } from 'react'
import appFunctionAPI, {
  ApplicationFunctionPropertyConfiguration,
} from '../../lib/commons/appFunction'
import BoolExpression from '../../utils/boolExpression'

export interface ExtensionInfo {
  propertyConfigurations: ApplicationFunctionPropertyConfiguration[]
}

export const useEntityExtension = (
  functionUuid: string,
  projectUuid: string | undefined,
  ticketType?: string | undefined,
  ticketListUuid?: string | undefined
) => {
  const [extensions, setExtensions] = useState<ExtensionInfo | undefined>()

  const groupKeys: string[] = useMemo(() => {
    return [projectUuid, ticketType, ticketListUuid].filter(
      v => !!v
    ) as string[]
  }, [projectUuid, ticketType, ticketListUuid])

  const fetchPropertyConfigurations = useCallback(async () => {
    const response = await appFunctionAPI.getPropertyConfiguraitons(
      functionUuid,
      groupKeys
    )
    const configs = response.json.map(v => ({
      ...v,
      requiredIf: v.requiredIf ? BoolExpression.of(v.requiredIf) : undefined,
      editableIfC: v.editableIfC ? BoolExpression.of(v.editableIfC) : undefined,
      editableIfU: v.editableIfU ? BoolExpression.of(v.editableIfU) : undefined,
      hiddenIfC: v.hiddenIfC ? BoolExpression.of(v.hiddenIfC) : undefined,
      hiddenIfU: v.hiddenIfU ? BoolExpression.of(v.hiddenIfU) : undefined,
    })) as ApplicationFunctionPropertyConfiguration[]

    return configs
  }, [functionUuid, groupKeys])

  const clearExtensions = useCallback(() => setExtensions(undefined), [])

  useEffect(
    () => {
      if (!projectUuid) return

      const fetch = async () => {
        const propertyConfigurations = await fetchPropertyConfigurations()
        if (propertyConfigurations) {
          setExtensions({
            propertyConfigurations,
          })
        }
      }
      clearExtensions()
      fetch()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectUuid, ticketListUuid]
  )

  const result = useMemo(
    () => ({
      extensions,
      clearExtensions,
    }),
    [extensions, clearExtensions]
  )

  return result
}
